import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  Download,
  UserPlus,
  UserMinus,
  Trash2,
  Key,
  Mail,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
} from 'lucide-react';
import { usersApi } from '@/api/endpoints';
import { User } from '@/types';
import { toast } from '@/utils/toast';
import { ConfirmationModal } from '@azyntor-labs/nexus-ui';

type Tab = 'import' | 'activate' | 'delete' | 'reset' | 'email' | 'export';

export const BulkUserOperationsPage = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<Tab>('import');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<{ total: number; success: number; failed: number; errors: string[] } | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [confirmAction, setConfirmAction] = useState<string | null>(null);

  const { data: usersRes } = useQuery({
    queryKey: ['users-bulk', userSearch],
    queryFn: () => usersApi.list({ page: 1, per_page: 100 }),
    staleTime: 30000,
  });

  const users: User[] = Array.isArray(usersRes?.data?.data) ? usersRes.data.data : [];
  const filteredUsers = users.filter((u) =>
    !userSearch ||
    (u.email || '').toLowerCase().includes(userSearch.toLowerCase()) ||
    (`${u.first_name} ${u.last_name}`).toLowerCase().includes(userSearch.toLowerCase())
  );

  const toggleUser = (id: string) => {
    setSelectedUserIds((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map((u) => u.id));
    }
  };

  const importMutation = useMutation({
    mutationFn: (formData: FormData) => usersApi.bulkImport(formData),
    onSuccess: (res) => {
      const data = res?.data?.data as any;
      setImportResult(data || { total: 0, success: 0, failed: 0, errors: [] });
      toast.success(`${data?.success || 0} users imported`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['users-bulk'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Import failed'),
  });

  const activateMutation = useMutation({
    mutationFn: () => usersApi.bulkActivate(selectedUserIds),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['users-bulk'] });
      toast.success(`${(res?.data?.data as any)?.count || selectedUserIds.length} users activated`);
      setSelectedUserIds([]);
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Activation failed'),
  });

  const deactivateMutation = useMutation({
    mutationFn: () => usersApi.bulkDeactivate(selectedUserIds),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['users-bulk'] });
      toast.success(`${(res?.data?.data as any)?.count || selectedUserIds.length} users deactivated`);
      setSelectedUserIds([]);
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Deactivation failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => usersApi.bulkDelete(selectedUserIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-bulk'] });
      toast.success('Users deleted');
      setSelectedUserIds([]);
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Delete failed'),
  });

  const resetMutation = useMutation({
    mutationFn: () => usersApi.bulkResetPassword(selectedUserIds),
    onSuccess: () => {
      toast.success('Password reset emails sent');
      setSelectedUserIds([]);
      setConfirmAction(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Reset failed'),
  });

  const emailMutation = useMutation({
    mutationFn: () => usersApi.bulkSendEmail(selectedUserIds, { subject: emailSubject, body: emailBody }),
    onSuccess: () => {
      toast.success('Emails sent');
      setSelectedUserIds([]);
      setEmailSubject('');
      setEmailBody('');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Email send failed'),
  });

  const handleImport = () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('file', selectedFile);
    importMutation.mutate(formData);
  };

  const handleExport = async () => {
    try {
      const response = await usersApi.exportAll();
      const blob = new Blob([response.data as any], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users-export.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Users exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: 'import', label: 'Import', icon: Upload },
    { key: 'activate', label: 'Activate/Deactivate', icon: UserPlus },
    { key: 'delete', label: 'Delete', icon: Trash2 },
    { key: 'reset', label: 'Reset Passwords', icon: Key },
    { key: 'email', label: 'Send Email', icon: Mail },
    { key: 'export', label: 'Export', icon: Download },
  ];

  const UserSelectionList = ({ actionButton }: { actionButton: React.ReactNode }) => (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          type="text"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder="Search users..."
          className="input-dark w-full pl-9"
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <input type="checkbox" checked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0} onChange={toggleAll} className="rounded" />
          <span className="text-sm text-white/60">Select all ({filteredUsers.length})</span>
        </div>
        <span className="text-sm text-white/50">{selectedUserIds.length} selected</span>
      </div>

      <div className="max-h-64 overflow-y-auto space-y-1">
        {filteredUsers.map((user) => (
          <label key={user.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-white/[0.02] cursor-pointer">
            <input type="checkbox" checked={selectedUserIds.includes(user.id)} onChange={() => toggleUser(user.id)} className="rounded" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white/90 truncate">{user.first_name} {user.last_name}</p>
              <p className="text-xs text-white/50 truncate">{user.email}</p>
            </div>
          </label>
        ))}
      </div>

      {selectedUserIds.length > 0 && actionButton}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white/90">Bulk User Operations</h1>
        <p className="text-sm text-white/50 mt-1">Manage users in bulk</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-white/[0.03] rounded-lg p-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSelectedUserIds([]); }}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.key ? 'bg-teal-500/20 text-teal-400' : 'text-white/60 hover:text-white/90'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="card-dark p-6">
        {/* Import Tab */}
        {activeTab === 'import' && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-white/90 mb-4">Import Users from CSV</h3>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-white/[0.08] rounded-xl p-8 text-center cursor-pointer hover:border-white/[0.16] transition-all"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              {selectedFile ? (
                <div className="flex items-center justify-center space-x-2">
                  <Upload className="w-5 h-5 text-teal-400" />
                  <span className="text-sm text-white/90">{selectedFile.name}</span>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
                  <p className="text-sm text-white/50">Click to select CSV or Excel file</p>
                </>
              )}
            </div>
            <button onClick={handleImport} disabled={!selectedFile || importMutation.isPending} className="btn-primary px-4 py-2 rounded-lg text-sm disabled:opacity-50">
              {importMutation.isPending ? 'Importing...' : 'Import Users'}
            </button>

            {importResult && (
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 space-y-2">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2"><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-sm text-white/80">{importResult.success} imported</span></div>
                  <div className="flex items-center space-x-2"><XCircle className="w-4 h-4 text-red-400" /><span className="text-sm text-white/80">{importResult.failed} failed</span></div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="space-y-1">
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <div key={i} className="flex items-start space-x-2"><AlertCircle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" /><span className="text-xs text-white/60">{err}</span></div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Activate/Deactivate Tab */}
        {activeTab === 'activate' && (
          <UserSelectionList
            actionButton={
              <div className="flex space-x-3">
                <button onClick={() => setConfirmAction('activate')} className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
                  <UserPlus className="w-4 h-4" /><span>Activate ({selectedUserIds.length})</span>
                </button>
                <button onClick={() => setConfirmAction('deactivate')} className="btn-outline flex items-center space-x-2 px-4 py-2 rounded-lg text-sm text-amber-400 border-amber-500/30">
                  <UserMinus className="w-4 h-4" /><span>Deactivate ({selectedUserIds.length})</span>
                </button>
              </div>
            }
          />
        )}

        {/* Delete Tab */}
        {activeTab === 'delete' && (
          <UserSelectionList
            actionButton={
              <button onClick={() => setConfirmAction('delete')} className="bg-red-500 hover:bg-red-600 text-white flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
                <Trash2 className="w-4 h-4" /><span>Delete ({selectedUserIds.length})</span>
              </button>
            }
          />
        )}

        {/* Reset Passwords Tab */}
        {activeTab === 'reset' && (
          <UserSelectionList
            actionButton={
              <button onClick={() => setConfirmAction('reset')} className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
                <Key className="w-4 h-4" /><span>Reset Passwords ({selectedUserIds.length})</span>
              </button>
            }
          />
        )}

        {/* Send Email Tab */}
        {activeTab === 'email' && (
          <div className="space-y-4">
            <UserSelectionList actionButton={<></>} />
            {selectedUserIds.length > 0 && (
              <>
                <div>
                  <label className="block text-sm text-white/70 mb-1">Subject</label>
                  <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="input-dark w-full" placeholder="Email subject..." />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-1">Body</label>
                  <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} className="input-dark w-full" rows={5} placeholder="Email body..." />
                </div>
                <button onClick={() => emailMutation.mutate()} disabled={!emailSubject || !emailBody || emailMutation.isPending} className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-lg text-sm disabled:opacity-50">
                  <Mail className="w-4 h-4" /><span>{emailMutation.isPending ? 'Sending...' : `Send to ${selectedUserIds.length} users`}</span>
                </button>
              </>
            )}
          </div>
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60 mb-4">Export all users as CSV</p>
            <button onClick={handleExport} className="btn-primary flex items-center space-x-2 px-6 py-2 rounded-lg text-sm mx-auto">
              <Download className="w-4 h-4" /><span>Export All Users</span>
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction === 'activate') activateMutation.mutate();
          else if (confirmAction === 'deactivate') deactivateMutation.mutate();
          else if (confirmAction === 'delete') deleteMutation.mutate();
          else if (confirmAction === 'reset') resetMutation.mutate();
        }}
        title={`${confirmAction ? confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1) : ''} Users?`}
        message={`This will ${confirmAction} ${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''}.${confirmAction === 'delete' ? ' This action cannot be undone.' : ''}`}
        variant={confirmAction === 'delete' ? 'danger' : 'warning'}
        confirmText={confirmAction ? confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1) : 'Confirm'}
        cancelText="Cancel"
      />
    </div>
  );
};
